import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { labelsApi, meApi } from '../services/api';
import type { Label } from '../types/label';

const meLabelsKey = ['me', 'labels'] as const;

/**
 * Single cross-space label fetch — backend's `/me/labels` returns every
 * label in every space the user belongs to in one shot. All other label
 * hooks below derive from this one cache so we only ever pay one request.
 */
export function useMeLabels() {
  return useQuery<Label[]>({
    queryKey: meLabelsKey,
    queryFn: () => meApi.labels(),
    staleTime: 60_000,
  });
}

/**
 * Active labels for one space, sorted by name. Archived labels are
 * filtered out so pickers/rails stay clean. Pass
 * `{ includeArchived: true }` to surface archived rows alongside active
 * ones (Labels management tab).
 */
export function useLabels(
  spaceId: string | null | undefined,
  opts?: { includeArchived?: boolean },
): {
  data?: Label[];
  isLoading: boolean;
} {
  const includeArchived = opts?.includeArchived ?? false;
  const { data, isLoading } = useMeLabels();
  const filtered = useMemo(() => {
    if (!data || !spaceId) return undefined;
    return data
      .filter((l) => l.spaceId === spaceId)
      .filter((l) => (includeArchived ? true : !l.archived))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, spaceId, includeArchived]);
  return { data: filtered, isLoading };
}

export interface AllLabels {
  /** Active labels only, sorted by name — what pickers/rails should iterate. */
  all: Label[];
  /** Active labels grouped by spaceId. */
  bySpace: Map<string, Label[]>;
  /**
   * Lookup by labelId — includes archived rows so `LabelChips` can still
   * hydrate labels attached to historical inbox items after archiving.
   */
  byId: Map<string, Label>;
  isLoading: boolean;
}

/**
 * Derived aggregate view of labels — used by the inbox label rail and
 * label chips. `bySpace`/`all` exclude archived (rails and pickers stay
 * lean), while `byId` retains them so chips on historical items still
 * render with their original name + color.
 */
export function useAllLabels(): AllLabels {
  const { data, isLoading } = useMeLabels();
  return useMemo(() => {
    const sorted = [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const all: Label[] = [];
    const bySpace = new Map<string, Label[]>();
    const byId = new Map<string, Label>();
    for (const lbl of sorted) {
      byId.set(lbl.labelId, lbl);
      if (lbl.archived) continue;
      all.push(lbl);
      const list = bySpace.get(lbl.spaceId) ?? [];
      list.push(lbl);
      bySpace.set(lbl.spaceId, list);
    }
    return { all, bySpace, byId, isLoading };
  }, [data, isLoading]);
}

interface CreateLabelArgs {
  spaceId: string;
  name: string;
  slug?: string;
  color?: string;
  description?: string;
}

export function useCreateLabel() {
  const queryClient = useQueryClient();
  return useMutation<Label, Error, CreateLabelArgs>({
    mutationFn: ({ spaceId, ...body }) => labelsApi.create(spaceId, body),
    onSuccess: (label) => {
      // Splice the new label into the aggregated cache so the rail and
      // picker update immediately without waiting for the refetch.
      queryClient.setQueryData<Label[]>(meLabelsKey, (prev) => {
        const list = prev ?? [];
        if (list.some((l) => l.labelId === label.labelId)) return list;
        return [...list, label].sort((a, b) => a.name.localeCompare(b.name));
      });
      queryClient.invalidateQueries({ queryKey: meLabelsKey });
    },
  });
}

interface UpdateLabelArgs {
  spaceId: string;
  labelId: string;
  name?: string;
  color?: string;
  description?: string;
  archived?: boolean;
}

export function useUpdateLabel() {
  const queryClient = useQueryClient();
  return useMutation<Label, Error, UpdateLabelArgs>({
    mutationFn: ({ spaceId, labelId, ...patch }) =>
      labelsApi.update(spaceId, labelId, patch),
    onSuccess: (label) => {
      queryClient.setQueryData<Label[]>(meLabelsKey, (prev) => {
        const list = prev ?? [];
        const idx = list.findIndex((l) => l.labelId === label.labelId);
        if (idx === -1) return list;
        const next = list.slice();
        next[idx] = label;
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      queryClient.invalidateQueries({ queryKey: meLabelsKey });
    },
  });
}
